// FILE: src/components/feedback/ManualFeedbackForm.js
//
// Admin manual-entry form for the Feedback Hub. Lets an admin log feedback
// received off-platform (Discord, Reddit, LinkedIn, DM) so everything lives in
// one repository. Writes via feedbackService.createManualFeedback.

import React, { useState } from 'react';
import {
  createManualFeedback, FEEDBACK_CATEGORIES, FEEDBACK_SEVERITIES, FEEDBACK_SOURCES,
} from '../../services/feedbackService';
import { getActiveCertIds } from '../../config/exams';
import { styles, certLabel } from './feedbackStyles';

const f = {
  panel: { background: '#fff', border: '1px solid #e0e0e0', borderRadius: '10px', padding: '22px 24px', maxWidth: '620px' },
  grid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' },
  field: { marginBottom: '16px' },
  msg: (ok) => ({ marginTop: '12px', fontSize: '0.86rem', color: ok ? '#2e7d32' : '#c62828' }),
};

export default function ManualFeedbackForm({ onCreated }) {
  const [category, setCategory] = useState('Feature Request');
  const [severity, setSeverity] = useState('Medium');
  const [source, setSource]     = useState('Discord');
  const [cert, setCert]         = useState('');
  const [title, setTitle]       = useState('');
  const [description, setDesc]  = useState('');
  const [busy, setBusy]         = useState(false);
  const [msg, setMsg]           = useState(null);

  const certs = getActiveCertIds();
  const canSubmit = description.trim().length > 0;

  async function submit() {
    if (!canSubmit) return;
    setBusy(true); setMsg(null);
    const row = await createManualFeedback({
      category, severity, source,
      certification: cert || null,
      title: title || null,
      description,
      pageName: '(manual entry)',
      route: 'admin-manual',
    });
    setBusy(false);
    if (!row) { setMsg({ ok: false, text: 'Could not save — check console.' }); return; }
    setMsg({ ok: true, text: 'Feedback logged.' });
    setTitle(''); setDesc('');
    if (onCreated) onCreated(row);
  }

  return (
    <div style={f.panel}>
      <p style={{ fontSize: '0.85rem', color: '#666', marginTop: 0 }}>
        Log feedback received outside the app so it's tracked centrally.
      </p>
      <div style={f.grid}>
        <div style={f.field}>
          <label style={styles.label}>Category</label>
          <select style={{ ...styles.select, width: '100%' }} value={category} onChange={(e) => setCategory(e.target.value)}>
            {FEEDBACK_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div style={f.field}>
          <label style={styles.label}>Source</label>
          <select style={{ ...styles.select, width: '100%' }} value={source} onChange={(e) => setSource(e.target.value)}>
            {FEEDBACK_SOURCES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div style={f.field}>
          <label style={styles.label}>Severity</label>
          <select style={{ ...styles.select, width: '100%' }} value={severity} onChange={(e) => setSeverity(e.target.value)}>
            {FEEDBACK_SEVERITIES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div style={f.field}>
          <label style={styles.label}>Certification</label>
          <select style={{ ...styles.select, width: '100%' }} value={cert} onChange={(e) => setCert(e.target.value)}>
            <option value="">(none)</option>
            {certs.map((c) => <option key={c} value={c}>{certLabel(c)}</option>)}
          </select>
        </div>
      </div>
      <div style={f.field}>
        <label style={styles.label}>Title</label>
        <input style={{ ...styles.input, width: '100%', boxSizing: 'border-box' }} value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Short summary (optional)" />
      </div>
      <div style={f.field}>
        <label style={styles.label}>Description</label>
        <textarea style={styles.textarea} value={description} onChange={(e) => setDesc(e.target.value)} placeholder="What was the feedback?" data-testid="manual-feedback-description" />
      </div>
      <button style={styles.primaryBtn} disabled={busy || !canSubmit} onClick={submit}>
        {busy ? 'Saving…' : 'Log feedback'}
      </button>
      {msg && <p style={f.msg(msg.ok)}>{msg.text}</p>}
    </div>
  );
}
